import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { loginTrackingService } from '../services/loginTrackingService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // Core profile via a plain select of the non-phone columns. These are
        // always readable (whether or not the phone-PII migration is applied),
        // so the profile never blanks out.
        const { data, error } = await supabase
          ?.from('user_profiles')
          ?.select('id, email, title, first_name, last_name, country, institution, institution_type, department, academic_rank, orcid, website, subject_areas, roles, is_platform_admin, experience_level, avatar_url, bio, receive_updates, available_as_reviewer, reviewer_keywords, is_active, created_at, updated_at')
          ?.eq('id', userId)
          ?.single()

        if (!error && data) {
          // Own phone comes from a SECURITY DEFINER RPC (column-level SELECT is
          // revoked for regular reads). Best-effort: if the RPC isn't there yet,
          // the profile still loads without the phone.
          let phone = null
          try {
            const { data: me } = await supabase?.rpc('get_my_profile')?.single()
            phone = me?.phone ?? null
          } catch (_) { /* get_my_profile not deployed yet */ }

          // Convert snake_case to camelCase for state
          setUserProfile({
            id: data?.id,
            email: data?.email,
            title: data?.title,
            firstName: data?.first_name,
            lastName: data?.last_name,
            phone,
            country: data?.country,
            institution: data?.institution,
            institutionType: data?.institution_type,
            department: data?.department,
            academicRank: data?.academic_rank,
            orcid: data?.orcid,
            website: data?.website,
            subjectAreas: data?.subject_areas,
            roles: data?.roles,
            // Platform admin is global (analytics/stats), NOT a conference role.
            // Conference roles live in conference_roles, per conference.
            // `roles` fallback is transitional until the migration lands.
            isPlatformAdmin: data?.is_platform_admin ?? data?.roles?.includes('admin') ?? false,
            experienceLevel: data?.experience_level,
            avatarUrl: data?.avatar_url,
            bio: data?.bio,
            receiveUpdates: data?.receive_updates,
            availableAsReviewer: data?.available_as_reviewer,
            reviewerKeywords: data?.reviewer_keywords,
            isActive: data?.is_active,
            createdAt: data?.created_at,
            updatedAt: data?.updated_at
          })
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })?.catch((error) => {
      // Handle invalid session errors
      console.error('Session error:', error)
      if (error?.message?.includes('refresh_token_not_found') || 
          error?.message?.includes('Invalid Refresh Token') ||
          error?.message?.includes('Refresh Token Not Found')) {
        // Clear invalid session from localStorage
        localStorage.removeItem('supabase.auth.token')
        supabase?.auth?.signOut({ scope: 'local' })
        setUser(null)
        profileOperations?.clear()
      }
      setLoading(false)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        // Handle token refresh errors
        if (event === 'TOKEN_REFRESHED') {
          console.log('Token refreshed successfully')
        }
        if (event === 'SIGNED_IN' && session?.user?.id) {
          loginTrackingService?.trackLogin(session?.user?.id) // Fire-and-forget
        }
        if (event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true)
        }
        if (event === 'SIGNED_OUT') {
          setUser(null)
          setIsPasswordRecovery(false)
          profileOperations?.clear()
        }
        // Handle token refresh failures
        if (event === 'TOKEN_REFRESH_FAILED') {
          console.error('Token refresh failed, clearing session')
          localStorage.removeItem('supabase.auth.token')
          setUser(null)
          profileOperations?.clear()
        }
        authStateHandlers?.onChange(event, session)
      }
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: email?.trim()?.toLowerCase(),
        password
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfileData = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile: async (updates) => {
      if (!user) return { error: { message: 'No user logged in' } };
      
      try {
        const { data, error } = await supabase?.from('user_profiles')?.update({
          title: updates?.title,
          first_name: updates?.firstName,
          last_name: updates?.lastName,
          phone: updates?.phone,
          country: updates?.country,
          institution: updates?.institution,
          institution_type: updates?.institutionType,
          department: updates?.department,
          academic_rank: updates?.academicRank,
          orcid: updates?.orcid,
          website: updates?.website,
          subject_areas: updates?.subjectAreas,
          roles: updates?.roles,
          experience_level: updates?.experienceLevel,
          avatar_url: updates?.avatarUrl,
          bio: updates?.bio,
          receive_updates: updates?.receiveUpdates,
          available_as_reviewer: updates?.availableAsReviewer,
          reviewer_keywords: updates?.reviewerKeywords
        })?.eq('id', user?.id)?.select('id, email, title, first_name, last_name, country, institution, institution_type, department, academic_rank, orcid, website, subject_areas, roles, is_platform_admin, experience_level, avatar_url, bio, receive_updates, available_as_reviewer, reviewer_keywords, is_active, created_at, updated_at')?.single();

        if (!error && data) {
          // Convert snake_case to camelCase for state. `phone` isn't returned by
          // the select (column-level SELECT revoked), so keep the value we just
          // wrote.
          setUserProfile({
            id: data?.id,
            email: data?.email,
            title: data?.title,
            firstName: data?.first_name,
            lastName: data?.last_name,
            phone: updates?.phone,
            country: data?.country,
            institution: data?.institution,
            institutionType: data?.institution_type,
            department: data?.department,
            academicRank: data?.academic_rank,
            orcid: data?.orcid,
            website: data?.website,
            subjectAreas: data?.subject_areas,
            roles: data?.roles,
            // Platform admin is global (analytics/stats), NOT a conference role.
            // Conference roles live in conference_roles, per conference.
            // `roles` fallback is transitional until the migration lands.
            isPlatformAdmin: data?.is_platform_admin ?? data?.roles?.includes('admin') ?? false,
            experienceLevel: data?.experience_level,
            avatarUrl: data?.avatar_url,
            bio: data?.bio,
            receiveUpdates: data?.receive_updates,
            availableAsReviewer: data?.available_as_reviewer,
            reviewerKeywords: data?.reviewer_keywords,
            isActive: data?.is_active,
            createdAt: data?.created_at,
            updatedAt: data?.updated_at
          });
        }
        return { data, error };
      } catch (error) {
        return { error: { message: 'Network error. Please try again.' } };
      }
    },
    isAuthenticated: !!user,
    isPasswordRecovery
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
